import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { BlueprintChat, type BlueprintQuestion } from "@/components/blueprint/BlueprintChat";
import {
  useCreateGoal,
  useGoals,
  useProfile,
  useSaveGoalProfile,
  useSaveProfile,
} from "@/lib/queries";
import { TIER_2_OPENER, splitList, useBlueprintSave } from "@/lib/blueprint";

/**
 * 7a — the ICP blueprint, in two tiers.
 *
 * **Tier one** is the same five answers intake collects: buyer type, role,
 * company, pain, industry. Identical fields on purpose, writing to the same
 * `profiles` columns the radar and classifier already read, so it sharpens
 * what exists rather than opening a second description of her buyer.
 *
 * **Tier two** is what the feed sends her to when two pages of rooms have not
 * landed. It goes wider than the sentence — size, geography, the companies she
 * actually wants, where her real customers came from — and writes to
 * `customer_profiles`, the goal-scoped table built for exactly this and empty
 * until now.
 *
 * Two tiers rather than ten questions up front because the first five are the
 * price of entry and the next five have to be *earned*. A founder who has seen
 * rooms and found them wrong has a reason to answer them; the same questions
 * asked before she had seen anything are a form.
 */
export const Route = createFileRoute("/icp")({
  validateSearch: z.object({ tier: z.literal(2).optional() }),
  component: Icp,
});

/**
 * Three questions, not five.
 *
 * The conversation is the gate on the feed now, so its length is the price of
 * seeing the rooms — and a five-question price on a screen she reached by
 * tapping "unlock" is where she closes the tab. Three she finishes.
 *
 * The three that stayed are the three the product reads. `targetIndustry` is
 * the hard gate on the curated base (rule #2 — without it Omina cannot tell a
 * room full of her buyers from a room full of other founders, and it will not
 * guess), and `buyerRole` with `buyerCompany` are what the radar prints as her
 * ICP line and ranks against.
 *
 * The two that went were the two nothing ranked on. `audience` (business or
 * person) is answered implicitly by the other three, and `buyerPain` is
 * narrative — the best material in the set and the wrong thing to spend a
 * founder's patience on before she has seen a single room. Both are still on
 * the intake screen, which writes the same fields.
 */
const TIER_1: BlueprintQuestion[] = [
  {
    field: "buyerRole",
    ask: "Let's sharpen who actually buys. Who signs off? Their role, in your words rather than a job title.",
    hint: "“the head of risk”, “the clinic owner”",
    placeholder: "The head of risk…",
  },
  {
    field: "buyerCompany",
    ask: "And what kind of company are they sitting in?",
    hint: "“mid-size brokerages”, “clinics with two or three locations”",
    placeholder: "Mid-size brokerages…",
  },
  {
    field: "targetIndustry",
    ask: "Last one. Which industry are they in?",
    hint: "The industry they work in, not yours.",
    placeholder: "Fintech…",
  },
];

/**
 * Tier two. Every one of these is a filter the ranking can actually use, and
 * `dreamCustomers` is the most valuable answer in either tier — it is the only
 * one that names real companies, which is what turns a ranked list into a
 * reason to walk into a specific room.
 */
const TIER_2: BlueprintQuestion[] = [
  {
    field: "companySizeRange",
    ask: "How big are the companies that actually buy? Not the ones you would like — the ones that close.",
    choices: [
      { value: "solo", label: "Solo" },
      { value: "startup", label: "Startups" },
      { value: "smb", label: "Small business" },
      { value: "mid-market", label: "Mid-market" },
      { value: "enterprise", label: "Enterprise" },
    ],
    placeholder: "Pick one above",
  },
  {
    field: "dreamCustomers",
    ask: "Name the companies you would most want to land. Three is plenty.",
    hint: "Real names. This is the answer that points at specific rooms.",
    placeholder: "Stripe, Wise, Nubank…",
  },
  {
    field: "acquisitionChannels",
    ask: "Where did your best customers actually come from so far?",
    hint: "Introductions, an event, cold outbound, inbound — whatever is true, even if it is “luck”.",
    placeholder: "Two came from one dinner…",
  },
  {
    field: "targetGeos",
    ask: "Where are they, geographically? Cities or regions.",
    hint: "This decides which city's rooms are worth a flight.",
    placeholder: "Bay Area and New York…",
  },
  {
    field: "painPoints",
    ask: "Last one, and it is the sharpest. What do they try *before* they find you — and why does it fail?",
    hint: "The alternative you beat is what a good room is really about.",
    placeholder: "They hire a second ops person and it still breaks…",
  },
];

function Icp() {
  const { tier } = Route.useSearch();
  const isDeep = tier === 2;

  const profile = useProfile();
  const saveProfile = useSaveProfile();
  const goals = useGoals();
  const createGoal = useCreateGoal();
  const saveGoalProfile = useSaveGoalProfile();
  const navigate = useNavigate();

  const deepSave = useBlueprintSave({
    type: "cust",
    goals,
    createGoal,
    saveGoalProfile,
/**
     * The feed, not the five.
     *
     * She has just spent a conversation describing exactly who she sells to, and
     * `/matches` answers that with three rooms and two behind a lock — the screen
     * whose job is to prove the product works to someone who has not paid, and
     * whose next control is a button back to the radar to scan again. Landing
     * there after the questions reads as being sent to the queue after doing the
     * work.
     *
     * The feed is what the answers actually bought: every room that ranks, in the
     * order the blueprint just changed.
     */
    onDone: () => void navigate({ to: "/feed" }),
  });

  return (
    <BlueprintChat
      title={isDeep ? "Sharpening your matches" : "Your ICP blueprint"}
      opener={
        isDeep
          ? TIER_2_OPENER
          : "A few questions to unlock better matches, based on what you actually need. Answer them out loud or type — either is fine."
      }
      questions={isDeep ? TIER_2 : TIER_1}
      saving={isDeep ? deepSave.saving : saveProfile.isPending}
      error={
        isDeep
          ? deepSave.error
          : saveProfile.isError
            ? saveProfile.error.message
            : null
      }
      doneTitle="That is the blueprint."
      doneNote={
        isDeep
          ? "Omina now knows the size, the geography and the names. The next scan is ranked against all of it."
          : "It sharpens the buyer on your profile, which is what every room is ranked against from here."
      }
      saveLabel={isDeep ? "Save and rescan" : "Save and see my rooms"}
      /* The feed either way — it is where both tiers are entered from now.
         Tier one used to return to `/matches`, which was true when the nudge
         lived there and is a detour now. */
      exitTo="/feed"
      onSave={(a) => {
        if (isDeep) {
          deepSave.run({
            ...(a.companySizeRange ? { companySizeRange: a.companySizeRange } : {}),
            ...(splitList(a.dreamCustomers) ? { dreamCustomers: splitList(a.dreamCustomers) } : {}),
            ...(splitList(a.acquisitionChannels)
              ? { acquisitionChannels: splitList(a.acquisitionChannels) }
              : {}),
            ...(splitList(a.targetGeos) ? { targetGeos: splitList(a.targetGeos) } : {}),
            ...(a.painPoints ? { painPoints: a.painPoints } : {}),
          });
          return;
        }

        /**
         * Only the answered fields. An empty string would overwrite a good
         * answer from intake with nothing, which is the quiet way a re-run of
         * this screen could leave her profile worse than before it.
         */
        const patch = Object.fromEntries(
          Object.entries(a).filter(([, v]) => v.trim().length > 0),
        );
        saveProfile.mutate({ ...profile.data, ...patch } as never, {
          // The feed, not the five — see the note on the deep save above.
          onSuccess: () => void navigate({ to: "/feed" }),
        });
      }}
    />
  );
}
